import { useState } from "react";
import { api, openFile } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { CheckCircle2, Clock3, XCircle, Check, X, FileText, MessageSquare } from "lucide-react";

const STATUS = {
  approved: { label: "Aprobada", icon: CheckCircle2, cls: "bg-primary/12 text-primary" },
  pending: { label: "Pendiente", icon: Clock3, cls: "bg-gold/15 text-[#a9741c] dark:text-gold" },
  rejected: { label: "Rechazada", icon: XCircle, cls: "bg-destructive/12 text-destructive" },
};
const fmt = (d) => new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });

function DecisionDialog({ open, onOpenChange, req, action, onDone }) {
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const approve = action === "approve";

  const submit = async () => {
    setSaving(true);
    try {
      if (approve) await api.post(`/hr/vacations/${req.id}/approve`, { message });
      else await api.post(`/hr/vacations/${req.id}/reject`, { reason: message });
      toast.success(approve ? "Solicitud aprobada · se ha avisado al empleado" : "Solicitud rechazada · se ha avisado al empleado");
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{approve ? "Aprobar solicitud" : "Rechazar solicitud"}</DialogTitle>
          <DialogDescription>
            {req.user_name} · {req.type} · {req.from_date === req.to_date ? fmt(req.from_date) : `${fmt(req.from_date)} → ${fmt(req.to_date)}`} ({req.days} días)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label>Mensaje para el empleado {approve ? "(opcional)" : "(motivo)"}</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="resize-none" rows={4}
            placeholder={approve ? "Ej.: Aprobadas, ¡buen descanso!" : "Explica el motivo del rechazo…"} data-testid="decision-message-input" />
          <p className="text-xs text-muted-foreground">El empleado recibirá este mensaje por email y en la campana de avisos.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className={cn(!approve && "bg-destructive hover:bg-destructive/90")} data-testid="decision-confirm-btn">
            {saving ? "Enviando…" : approve ? "Aprobar y avisar" : "Rechazar y avisar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SolicitudesVacaciones() {
  const { user } = useAuth();
  const [status, setStatus] = useState("pending");
  const params = status !== "all" ? { status } : {};
  const { data: reqs, loading, refetch } = useFetch("/hr/vacations/requests", params);
  const canApprove = ["Administrador", "Directora"].includes(user?.role);
  const [decision, setDecision] = useState(null); // { req, action }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="solicitudes-page">
      <PageHeader title="Solicitudes de vacaciones" subtitle={canApprove ? "Aprueba o rechaza las peticiones del equipo (el empleado recibe aviso automático)" : "Consulta el estado de las peticiones del equipo"} />

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList data-testid="req-filter">
          <TabsTrigger value="pending">Pendientes</TabsTrigger>
          <TabsTrigger value="approved">Aprobadas</TabsTrigger>
          <TabsTrigger value="rejected">Rechazadas</TabsTrigger>
          <TabsTrigger value="all">Todas</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="border-border shadow-none overflow-hidden">
        {loading || !reqs ? (
          <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : reqs.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No hay solicitudes en este estado.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Empleado</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead className="text-center">Días</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Justif.</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right pr-5">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reqs.map((r) => {
                const st = STATUS[r.status];
                return (
                  <TableRow key={r.id} data-testid={`req-row-${r.id}`}>
                    <TableCell className="pl-5">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarImage src={r.user_picture} /><AvatarFallback className="bg-primary/10 text-primary text-xs">{(r.user_name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                        <span className="font-medium text-sm">{r.user_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{r.from_date === r.to_date ? fmt(r.from_date) : `${fmt(r.from_date)} → ${fmt(r.to_date)}`}</TableCell>
                    <TableCell className="text-center text-sm tabular-nums">{r.days}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.type}</TableCell>
                    <TableCell>
                      {r.justificante ? (
                        <button className="text-xs text-primary hover:underline inline-flex items-center gap-1" onClick={() => openFile(`/hr/vacations/${r.id}/justificante`)} data-testid={`req-justif-${r.id}`}>
                          <FileText className="h-3.5 w-3.5" /> Ver
                        </button>
                      ) : r.justificante_required ? (
                        <span className="text-xs text-gold">Pendiente</span>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium", st.cls)}><st.icon className="h-3.5 w-3.5" /> {st.label}</span>
                      {r.approver_message && (
                        <div className="text-[11px] text-muted-foreground mt-1 inline-flex items-start gap-1 max-w-[220px]"><MessageSquare className="h-3 w-3 mt-0.5 shrink-0" /> <span>{r.approver_message}</span></div>
                      )}
                    </TableCell>
                    <TableCell className="text-right pr-5">
                      {r.status === "pending" && canApprove ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" className="gap-1 h-8" onClick={() => setDecision({ req: r, action: "approve" })} data-testid={`req-approve-${r.id}`}><Check className="h-4 w-4" /> Aprobar</Button>
                          <Button size="sm" variant="outline" className="gap-1 h-8 text-destructive" onClick={() => setDecision({ req: r, action: "reject" })} data-testid={`req-reject-${r.id}`}><X className="h-4 w-4" /> Rechazar</Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">{r.approver_name || "—"}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {decision && (
        <DecisionDialog
          open={!!decision}
          onOpenChange={(o) => !o && setDecision(null)}
          req={decision.req}
          action={decision.action}
          onDone={refetch}
        />
      )}
    </div>
  );
}
